/**
 * Account security (/account/security): login alerts, where you are signed in, and your devices.
 *
 * Also exports the pieces App uses elsewhere: the banner shown when a login alert arrives, the
 * desktop notification call, and the screen shown after "That wasn't me".
 */

import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  confirmLoginAlert,
  fetchSecurity,
  rejectLoginAlert,
  signOutEverywhere,
  signOutSession,
  type LoginAlert,
} from "../api";
import { formatRelative, formatTimestamp } from "../format";
import { confirmDialog } from "./Dialog";

/** "Chrome on Windows · 203.0.113.x · 14:02" */
export function alertSummary(alert: LoginAlert): string {
  return `${alert.device} · ${alert.location} · ${formatTimestamp(alert.time)}`;
}

/** Shows a real Windows notification when running in the desktop app; does nothing in a browser. */
export function notifyLoginAlert(alert: LoginAlert): void {
  void window.PrismStaffApp?.notify?.(
    "New sign-in to your staff account",
    `${alertSummary(alert)}. Not you? Click to secure your account.`,
    `/account/security?alert=${alert.id}`,
  );
}

export interface RejectOutcome {
  sessionsEnded: number;
  passwordResetRequired: boolean;
}

/** Shared by the banner and the security page. Asks first; returns the outcome, or null if cancelled. */
export async function rejectAlert(alert: LoginAlert, csrfToken: string): Promise<RejectOutcome | null> {
  const ok = await confirmDialog({
    title: "Secure your account?",
    message:
      "Every session on this account is signed out straight away, including this one, and every device " +
      "stops being trusted. You will have to set a new password when you sign back in.",
    confirmLabel: "Sign out everything",
    danger: true,
  });
  if (!ok) return null;
  const result = await rejectLoginAlert(alert.id, csrfToken);
  return { sessionsEnded: result.sessionsEnded, passwordResetRequired: result.passwordResetRequired };
}

// ---- banner ---------------------------------------------------------------------------------------

export function LoginAlertBanner({
  alert,
  csrfToken,
  onResolved,
  onRejected,
}: {
  alert: LoginAlert;
  csrfToken: string;
  onResolved: () => void;
  onRejected: (outcome: RejectOutcome) => void;
}): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const act = async (action: () => Promise<void>): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "That did not work.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="login-alert" role="alert">
      <div className="login-alert-icon" aria-hidden="true">!</div>
      <div className="login-alert-text">
        <b>New sign-in to your staff account</b>
        <span>{alertSummary(alert)}. Not you? Secure your account now.</span>
        {error && <span className="login-alert-error">{error}</span>}
      </div>
      <button
        className="btn"
        type="button"
        disabled={busy}
        onClick={() =>
          void act(async () => {
            await confirmLoginAlert(alert.id, csrfToken);
            onResolved();
          })
        }
      >
        This was me
      </button>
      <button
        className="btn"
        data-variant="danger"
        type="button"
        disabled={busy}
        onClick={() =>
          void act(async () => {
            const outcome = await rejectAlert(alert, csrfToken);
            if (outcome) onRejected(outcome);
          })
        }
      >
        That wasn't me
      </button>
    </div>
  );
}

// ---- after "That wasn't me" -----------------------------------------------------------------------

export function SecuredScreen({ outcome }: { outcome: RejectOutcome }): JSX.Element {
  return (
    <div className="login">
      <div className="login-card login-form">
        <div className="rail-mark">P</div>
        <h1>Your account is secured</h1>
        <p>
          {outcome.sessionsEnded > 0
            ? `${outcome.sessionsEnded} session${outcome.sessionsEnded === 1 ? " was" : "s were"} signed out, including this one, `
            : "Every session was signed out, "}
          and no device is trusted any more.
        </p>
        <p>
          {outcome.passwordResetRequired
            ? "When you sign back in you must choose a new password before you can do anything else."
            : "This account is defined in the server's .env file — ask whoever manages the server to change its password there."}
        </p>
        <p>The admins can see this in the audit log.</p>
        <button className="btn" data-variant="primary" type="button" onClick={() => window.location.assign("/")}>
          Sign in again
        </button>
      </div>
    </div>
  );
}

// ---- the page -------------------------------------------------------------------------------------

export function SecurityTab({
  csrfToken,
  focusAlertId,
  onRejected,
  onSignedOutSelf,
}: {
  csrfToken: string;
  focusAlertId: string;
  onRejected: (outcome: RejectOutcome) => void;
  onSignedOutSelf: () => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const security = useQuery({ queryKey: ["security"], queryFn: fetchSecurity, refetchInterval: 30_000 });
  const [error, setError] = useState<string | null>(null);

  const refresh = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: ["security"] });
    await queryClient.invalidateQueries({ queryKey: ["open-alerts"] });
  };
  const act = async (action: () => Promise<void>): Promise<void> => {
    setError(null);
    try {
      await action();
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "That did not work.");
    }
  };

  const data = security.data;
  const open = (data?.alerts ?? []).filter((a) => a.status === "open");
  const focused = data?.alerts.find((a) => String(a.id) === focusAlertId) ?? null;

  return (
    <div className="security">
      {error && (
        <div className="login-error" role="alert">
          {error}
        </div>
      )}

      {focused && focused.status !== "open" && (
        <div className="evidence-ok">
          That alert ({alertSummary(focused)}) was already marked{" "}
          {focused.status === "confirmed" ? "“this was me”" : "“that wasn't me”"}.
        </div>
      )}

      <h2 className="section-title">
        Login alerts <span className="portal-count">{open.length} open</span>
      </h2>
      {open.length === 0 && <p className="users-note">No unanswered sign-ins from new devices.</p>}
      {open.map((alert) => (
        <div key={alert.id} className="security-alert" data-focus={String(alert.id) === focusAlertId}>
          <div>
            <b>{alert.device}</b>
            <div className="users-note">
              {alert.location} · {formatTimestamp(alert.time)} ({formatRelative(alert.time)})
            </div>
          </div>
          <span className="grow" />
          <button
            className="btn"
            type="button"
            onClick={() =>
              void act(async () => {
                await confirmLoginAlert(alert.id, csrfToken);
                await refresh();
              })
            }
          >
            This was me
          </button>
          <button
            className="btn"
            data-variant="danger"
            type="button"
            onClick={() =>
              void act(async () => {
                const outcome = await rejectAlert(alert, csrfToken);
                if (outcome) onRejected(outcome);
              })
            }
          >
            That wasn't me
          </button>
        </div>
      ))}

      <h2 className="section-title">
        Where you're signed in <span className="portal-count">{data?.sessions.length ?? "…"}</span>
        <span className="grow" />
        <button
          className="btn"
          data-variant="danger"
          type="button"
          onClick={() =>
            void act(async () => {
              const ok = await confirmDialog({
                title: "Sign out everywhere?",
                message: "Every session on this account ends, including this one.",
                confirmLabel: "Sign out everywhere",
                danger: true,
              });
              if (!ok) return;
              await signOutEverywhere(csrfToken);
              onSignedOutSelf();
            })
          }
        >
          Sign out everywhere
        </button>
      </h2>
      <div className="tablewrap portal-table">
        <table>
          <thead>
            <tr>
              <th>Device</th>
              <th>Network</th>
              <th>Signed in</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {data?.sessions.map((s) => (
              <tr key={s.handle}>
                <td>
                  <b>{s.device}</b>
                  {s.current && <span className="portal-active">this session</span>}
                </td>
                <td className="mono">{s.ip ?? "-"}</td>
                <td className="mono">{s.createdAt ? formatTimestamp(s.createdAt) : "-"}</td>
                <td style={{ textAlign: "right" }}>
                  <button
                    className="btn"
                    type="button"
                    onClick={() =>
                      void act(async () => {
                        const result = await signOutSession(s.handle, csrfToken);
                        if (result.signedOutSelf) onSignedOutSelf();
                        else await refresh();
                      })
                    }
                  >
                    Sign out
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h2 className="section-title">Devices you've used</h2>
      <div className="tablewrap portal-table">
        <table>
          <thead>
            <tr>
              <th>Device</th>
              <th>Network</th>
              <th>First seen</th>
              <th>Last seen</th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {data && data.devices.length === 0 && (
              <tr>
                <td colSpan={5} className="empty">
                  Devices are recorded from your next sign-in.
                </td>
              </tr>
            )}
            {data?.devices.map((d) => (
              <tr key={d.id}>
                <td>
                  <b>{d.description}</b>
                  {d.current && <span className="portal-active">this device</span>}
                </td>
                <td className="mono">{d.ip ?? "-"}</td>
                <td className="mono">{formatTimestamp(d.firstSeen)}</td>
                <td className="mono">{formatRelative(d.lastSeen)}</td>
                <td>{d.trusted ? "Trusted" : <span style={{ color: "var(--warn)" }}>Not yet confirmed</span>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="users-note">Networks are shown with the last part hidden. The full address is never stored.</p>
    </div>
  );
}
